import { PdfViewerPageLayer } from '../components/pdf-viewer-page/pdf-viewer-page.component.type';
import { PdfViewerEventType } from '../+types/pdf-viewer-event.type';
import { debounce } from 'lodash-es';
import { PageActionType, determinePageAction } from './pdf-viewer-layout-helper';
import { PdfViewerPageEventType } from '../components/pdf-viewer-page/pdf-viewer-page-events.type';
import { PdfViewerLayoutSinglePageClasses, PdfViewerLayoutSinglePageDataTypes, PdfViewerLayoutSinglePageAttributes } from './pdf-viewer-layout-single-page.type';
import { PdfViewerLayoutType } from '../+types/pdf-viewer-layout.type';
export class PdfViewerLayoutSinglePage {
    constructor(eventBus, pdfViewerConfig) {
        this.eventBus = eventBus;
        this.pdfViewerConfig = pdfViewerConfig;
        this.type = PdfViewerLayoutType.OneColumn;
        this.pages = [];
        this.defaultPageDimensions = { width: 600, height: 600 };
        this._zoom = 1; // 1 = 100% document size
        this.userScrolled = false;
        this.fitToViewport = true; // turn off after zooming / TODO: turn back on when adding "fit to page" button
        // we don't want the scroll event to fire when setting the scroll manually
        this.bypassScrollEvent = false;
        this.pageWrapperElements = [];
        this.zoomIncrement = 0.2;
        this.onPreviewRendered = (e) => {
            const index = this.pages.findIndex(({ id }) => id === e.payload.pdfViewerPage.id);
            if (index >= 0) {
                this.calculateLayout(this.pages[index]);
            }
        };
        this.onPageRendered = () => {
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onPdfViewerPageLoadStateChangeEvent = (event) => {
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onPdfViewerPageLayerRenderStateChange = (event) => {
            this.debouncedOrchestratePagesOnNextTick();
        };
        this.onScroll = () => {
            // enable bypassing of the scroll event, when manually setting the scroll position
            if (this.bypassScrollEvent) {
                this.bypassScrollEvent = false;
                return;
            }
            this.debouncedOnParentUserScroll();
        };
        this.debouncedOnParentUserScroll = debounce(this.onParentUserScroll, 100);
        this.debouncedOrchestratePagesOnNextTick = debounce(this.orchestratePages, 0);
        this.debouncedOrchestratePagesForPageNavigation = debounce(this.debouncedOrchestratePagesOnNextTick, 100);
        this.debouncedOrchestratePagesForZoomAndResize = debounce(this.debouncedOrchestratePagesOnNextTick, 500);
    }
    init(parentElement, pages, pageIdFocused) {
        if (!parentElement)
            return;
        this.parentElement = parentElement;
        const htmlTemplate = `
            <div class="${PdfViewerLayoutSinglePageClasses.Layout}">
                <div ${PdfViewerLayoutSinglePageAttributes.DataType}="${PdfViewerLayoutSinglePageDataTypes.PageWrapper}" class="${PdfViewerLayoutSinglePageClasses.PageWrapper}"></div>
                <div ${PdfViewerLayoutSinglePageAttributes.DataType}="${PdfViewerLayoutSinglePageDataTypes.PageWrapper}" class="${PdfViewerLayoutSinglePageClasses.PageWrapper}"></div>
                <div ${PdfViewerLayoutSinglePageAttributes.DataType}="${PdfViewerLayoutSinglePageDataTypes.PageWrapper}" class="${PdfViewerLayoutSinglePageClasses.PageWrapper}"></div>
            </div>
        `;
        this.parentElement.innerHTML = htmlTemplate;
        this.containerElement = this.parentElement.querySelector(`.${PdfViewerLayoutSinglePageClasses.Layout}`);
        this.containerElement
            .querySelectorAll(`[${PdfViewerLayoutSinglePageAttributes.DataType}='${PdfViewerLayoutSinglePageDataTypes.PageWrapper}']`)
            .forEach(e => this.pageWrapperElements.push(e));
        this.initPages(pages);
        this.addEventListeners();
        this.goto(pageIdFocused);
    }
    initPages(pages) {
        this.pages = pages;
        this.pagesByPageId = new Map();
        this.pagesByPageNumber = new Map();
        this.pageIndexesByPageNumber = new Map();
        this.pages.forEach((page, index) => {
            this.pagesByPageId.set(page.id, page);
            this.pagesByPageNumber.set(page.pageNumber, page);
            this.pageIndexesByPageNumber.set(page.pageNumber, index);
            page.disableEmbeddedMediaAutoPlay = false;
        });
    }
    ensurePageWrapperContainers(pageNumber) {
        const currentPageNumbers = this.getCurrentPageNumbers();
        const requiredPageNumbers = [pageNumber, this.getNextPageNumber(pageNumber), this.getPreviousPageNumber(pageNumber)].filter(pageNumber => pageNumber !== null);
        const pageNumbersToReset = currentPageNumbers.filter(pageNumber => !requiredPageNumbers.includes(pageNumber));
        pageNumbersToReset.forEach((pageNumber) => {
            try {
                const page = this.pagesByPageNumber.get(pageNumber);
                if (page) {
                    page.reset();
                }
            }
            catch (e) {
                console.log(e);
            }
            this.resetPageWrapper(pageNumber);
        });
        requiredPageNumbers.forEach(id => {
            const wrapper = this.getPageWrapper(id);
            if (!wrapper) {
                this.allocatePageWrapper(id);
            }
        });
    }
    gotoPage(pageNumber) {
        if (pageNumber === this.currentPage) {
            return;
        }
        this.ensurePageWrapperContainers(pageNumber);
        this.resetScroll();
        const oldFocusedWrapper = this.parentElement.querySelector(`.${PdfViewerLayoutSinglePageClasses.FocusedWrapper}`);
        if (oldFocusedWrapper) {
            oldFocusedWrapper.classList.remove(PdfViewerLayoutSinglePageClasses.FocusedWrapper);
        }
        this.updateFocusedPage(pageNumber);
        this.setRenderLayers();
        const newFocusedWrapper = this.getPageWrapper(pageNumber);
        if (newFocusedWrapper) {
            newFocusedWrapper.classList.add(PdfViewerLayoutSinglePageClasses.FocusedWrapper);
        }
        this.calculateAllLayouts();
        this.debouncedOrchestratePagesForPageNavigation();
    }
    goto(pageId) {
        const page = this.pagesByPageId.get(pageId);
        if (page) {
            this.gotoPage(page.pageNumber);
        }
    }
    getCurrentPageNumbers() {
        return this.pageWrapperElements.reduce((pageNumbers, wrapper) => {
            const number = wrapper.getAttribute(PdfViewerLayoutSinglePageAttributes.PageNumber);
            if (number) {
                pageNumbers.push(Number.parseInt(number));
            }
            return pageNumbers;
        }, []);
    }
    get pagesInDescendingOrderOfPriority() {
        return [this.currentPage, this.getNextPageNumber(this.currentPage), this.getPreviousPageNumber(this.currentPage)]
            .filter(pageNumber => pageNumber !== null)
            .map(pageNumber => this.pagesByPageNumber.get(pageNumber));
    }
    orchestratePages() {
        const pageAction = determinePageAction(this.pagesInDescendingOrderOfPriority);
        if (!pageAction) {
            return;
        }
        const page = pageAction.payload.page;
        const wrapper = this.getPageWrapper(page.pageNumber);
        page.renderPageElement(wrapper);
        page.renderPreview();
        switch (pageAction.type) {
            case PageActionType.Load:
                // console.log('orchestratePages', page.pageNumber, pageAction.type);
                page.load();
                break;
            case PageActionType.Render:
                // console.log('orchestratePages', page.pageNumber, pageAction.type, pageAction.payload.layer);
                this.calculateLayout(page);
                page.renderLayer(pageAction.payload.layer);
                break;
        }
    }
    allocatePageWrapper(pageNumber) {
        const element = this.pageWrapperElements.find(element => element.hasAttribute(PdfViewerLayoutSinglePageAttributes.PageNumber) === false);
        if (element) {
            element.setAttribute(PdfViewerLayoutSinglePageAttributes.PageNumber, `${pageNumber}`);
            element.style.order = `${pageNumber}`;
            return element;
        }
    }
    resetPageWrapper(pageNumber) {
        const element = this.getPageWrapper(pageNumber);
        if (element) {
            element.innerHTML = '';
            element.removeAttribute(PdfViewerLayoutSinglePageAttributes.PageNumber);
            element.style.order = '';
            element.style.backgroundImage = '';
        }
    }
    getPageWrapper(pageNumber) {
        return this.pageWrapperElements.find(element => element.getAttribute(PdfViewerLayoutSinglePageAttributes.PageNumber) === `${pageNumber}`);
    }
    setRenderLayers() {
        const currentPage = this.pagesByPageNumber.get(this.currentPage);
        if (currentPage) {
            currentPage.requiredLayers = new Set([
                PdfViewerPageLayer.PdfCanvas,
                PdfViewerPageLayer.PdfText,
                PdfViewerPageLayer.PdfAnnotations,
                PdfViewerPageLayer.PdfEmbeddedMedia,
                PdfViewerPageLayer.Annotations
            ]);
        }
        const pages = [this.getNextPageNumber(this.currentPage), this.getPreviousPageNumber(this.currentPage)]
            .filter(pageNumber => pageNumber !== null)
            .map(pageNumber => this.pagesByPageNumber.get(pageNumber));
        pages.forEach(page => {
            page.requiredLayers = new Set([
                PdfViewerPageLayer.PdfCanvas,
                PdfViewerPageLayer.PdfText,
                PdfViewerPageLayer.PdfAnnotations,
                PdfViewerPageLayer.Annotations
            ]);
        });
    }
    updateFocusedPage(pageNumber) {
        const oldFocusedPage = this.pagesByPageNumber.get(this.currentPage);
        if (oldFocusedPage) {
            oldFocusedPage.blur();
        }
        this.currentPage = pageNumber;
        const page = this.pagesByPageNumber.get(pageNumber);
        if (page) {
            this.pageIdFocused = page.id;
            page.focus();
        }
    }
    next() {
        const nextPageNumber = this.getNextPageNumber(this.currentPage);
        if (nextPageNumber !== null) {
            this.gotoPage(nextPageNumber);
        }
    }
    previous() {
        const previousPageNumber = this.getPreviousPageNumber(this.currentPage);
        if (previousPageNumber !== null) {
            this.gotoPage(previousPageNumber);
        }
    }
    canNavigateToNext() {
        return this.getNextPageNumber(this.currentPage) !== null;
    }
    canNavigateToPrevious() {
        return this.getPreviousPageNumber(this.currentPage) !== null;
    }
    get zoom() {
        return this._zoom;
    }
    set zoom(zoomLevel) {
        if (!this.pdfViewerConfig.canZoom) {
            return;
        }
        const zoom = Math.max(Math.min(zoomLevel, this.pdfViewerConfig.maxZoomScale), this.pdfViewerConfig.minZoomScale);
        if (this._zoom === zoom) {
            return;
        }
        this._zoom = zoom;
        this.fitToViewport = false;
        this.calculateAllLayouts();
        this.resizeAllPages();
        this.debouncedOrchestratePagesForZoomAndResize();
    }
    zoomIn() {
        this.zoom = this.zoom + this.zoomIncrement;
    }
    zoomOut() {
        this.zoom = this.zoom - this.zoomIncrement;
    }
    resize() {
        if (this.userScrolled) {
            return;
        }
        this.calculateAllLayouts();
        this.resizeAllPages();
        this.debouncedOrchestratePagesForZoomAndResize();
    }
    destroy() {
        this._zoom = 1;
        this.pageIdFocused = null;
        this.userScrolled = false;
        this.resetPages();
        this.removeEventListeners();
        this.debouncedOnParentUserScroll.cancel();
        this.debouncedOrchestratePagesOnNextTick.cancel();
        this.debouncedOrchestratePagesForPageNavigation.cancel();
        this.debouncedOrchestratePagesForZoomAndResize.cancel();
        try {
            this.parentElement.removeChild(this.containerElement);
        }
        catch (_a) { }
        this.parentElement = null;
        this.containerElement = null;
    }
    resetPages() {
        this.pages.forEach(page => page.reset());
    }
    resizeAllPages() {
        const currentPageNumbers = this.getCurrentPageNumbers();
        for (let pageNumber of currentPageNumbers) {
            const page = this.pagesByPageNumber.get(pageNumber);
            if (page) {
                page.resize();
            }
        }
    }
    updatePageLayout(page, viewport) {
        const wrapper = this.getPageWrapper(page.pageNumber);
        if (!wrapper) {
            console.warn('Wrapper not found');
            return;
        }
        const dimensions = page.dimensions || this.defaultPageDimensions;
        page.renderPageElement(wrapper);
        const pageDimensions = this.fitToViewport ? this.fitDimensionsToViewport(dimensions, viewport) : this.applyZoom(dimensions);
        this.applyDimensionsToPage(page.pageElement, pageDimensions);
        this.adjustPagePositioning(pageDimensions, viewport);
    }
    calculateAllLayouts() {
        const viewport = { width: this.parentElement.clientWidth, height: this.parentElement.clientHeight };
        [this.currentPage, this.getNextPageNumber(this.currentPage), this.getPreviousPageNumber(this.currentPage)]
            .filter(pageNumber => pageNumber !== null)
            .map(pageNumber => this.pagesByPageNumber.get(pageNumber))
            .forEach(page => this.updatePageLayout(page, viewport));
    }
    calculateLayout(page) {
        const viewport = { width: this.parentElement.clientWidth, height: this.parentElement.clientHeight };
        this.updatePageLayout(page, viewport);
    }
    addEventListeners() {
        if (this.parentElement) {
            this.parentElement.addEventListener('scroll', this.onScroll);
        }
        this.eventBus.on(PdfViewerEventType.PreviewRendered, this.onPreviewRendered);
        this.eventBus.on(PdfViewerPageEventType.LoadStateChange, this.onPdfViewerPageLoadStateChangeEvent);
        this.eventBus.on(PdfViewerPageEventType.LayerRenderStateChange, this.onPdfViewerPageLayerRenderStateChange);
        this.eventBus.on(PdfViewerEventType.PageRendered, this.onPageRendered);
    }
    getNextPageNumber(pageNumber) {
        const nextPage = this.pages[this.pageIndexesByPageNumber.get(pageNumber) + 1];
        return nextPage ? nextPage.pageNumber : null;
    }
    getPreviousPageNumber(pageNumber) {
        const previousPage = this.pages[this.pageIndexesByPageNumber.get(pageNumber) - 1];
        return previousPage ? previousPage.pageNumber : null;
    }
    removeEventListeners() {
        if (this.parentElement) {
            this.parentElement.removeEventListener('scroll', this.onScroll);
        }
        this.eventBus.off(PdfViewerEventType.PreviewRendered, this.onPreviewRendered);
        this.eventBus.off(PdfViewerPageEventType.LoadStateChange, this.onPdfViewerPageLoadStateChangeEvent);
        this.eventBus.off(PdfViewerPageEventType.LayerRenderStateChange, this.onPdfViewerPageLayerRenderStateChange);
        this.eventBus.off(PdfViewerEventType.PageRendered, this.onPageRendered);
    }
    onParentUserScroll() {
        this.userScrolled = true;
        const page = this.pagesByPageNumber.get(this.currentPage);
        if (page) {
            this.calculateLayout(page);
        }
    }
    resetScroll() {
        this.bypassScrollEvent = true;
        this.parentElement.scrollTop = this.parentElement.scrollLeft = 0;
        this.userScrolled = true;
    }
    /**
     * In the single page view, the page must fit to the viewport by default.
     * We also take the current zoom level into account.
     */
    fitDimensionsToViewport(dimensions, viewport) {
        const margin = 10;
        const viewportAspectRatio = (viewport.width - 2 * margin) / (viewport.height - 2 * margin);
        const pageAspectRatio = dimensions.width / dimensions.height;
        let calculatedWidth = 0;
        let calculatedHeight = 0;
        if (viewportAspectRatio > pageAspectRatio) {
            // fit to viewport height
            calculatedHeight = viewport.height - 2 * margin;
            calculatedWidth = (viewport.height - 2 * margin) * pageAspectRatio;
        }
        else {
            // fit to viewport width
            calculatedWidth = viewport.width - 2 * margin;
            calculatedHeight = (viewport.width - 2 * margin) / pageAspectRatio;
        }
        this._zoom = calculatedWidth / dimensions.width;
        return { width: calculatedWidth, height: calculatedHeight };
    }
    applyZoom(dimensions) {
        let { width, height } = dimensions;
        width *= this.zoom;
        height *= this.zoom;
        return { width, height };
    }
    applyDimensionsToPage(pageElement, dimensions) {
        pageElement.style.width = `${dimensions.width}px`;
        pageElement.style.height = `${dimensions.height}px`;
    }
    /**
     * When the content grows beyond the viewport we need to adjust the flex positioning
     * to let the scroll area grow correctly. If we don't adjust the flex positioning,
     * you won't be able to scroll to the left or to the top.
     *
     * We also keep the content centered until the user manipulated the scroll.
     */
    adjustPagePositioning(pageDimensions, viewport) {
        const { width: calculatedWidth, height: calculatedHeight } = pageDimensions;
        const contentIsHigherThanViewport = calculatedHeight > viewport.height;
        const contentIsWiderThanViewport = calculatedWidth > viewport.width;
        if (contentIsHigherThanViewport) {
            this.parentElement.style.justifyContent = 'start';
            if (!this.userScrolled) {
                this.bypassScrollEvent = true;
                this.parentElement.scrollTop = (calculatedHeight - viewport.height) / 2;
            }
        }
        else {
            this.parentElement.style.justifyContent = 'center';
        }
        if (contentIsWiderThanViewport) {
            this.parentElement.style.alignItems = 'start';
            if (!this.userScrolled) {
                this.bypassScrollEvent = true;
                this.parentElement.scrollLeft = (calculatedWidth - viewport.width) / 2;
            }
        }
        else {
            this.parentElement.style.alignItems = 'center';
        }
        if (!contentIsWiderThanViewport && !contentIsHigherThanViewport) {
            this.userScrolled = false;
        }
    }
}
//# sourceMappingURL=pdf-viewer-layout-single-page.js.map